import {registerLocaleData} from '@angular/common';
import en from '@angular/common/locales/en';
import kk from '@angular/common/locales/kk';
import ru from '@angular/common/locales/ru';
import {loadTranslations} from '@angular/localize';
import {TUI_ENGLISH_LANGUAGE, TUI_RUSSIAN_LANGUAGE} from '@taiga-ui/i18n';
import type {AppLocale} from 'compob2b-libs/src/lib/i18n/locale';
import {translations} from 'compob2b-libs';
import {readUiLocale} from './ui-locale';

/**
 * Локали, для которых ЕСТЬ перевод ИНТЕРФЕЙСА (UI chrome): TS-словарь UI-строк
 * (тосты/конфиги форм/label'ы) + шаблонные $localize-переводы из
 * assets/i18n/<locale>.json. RU — встроенный дефолт (без оверлея), его здесь
 * нет.
 *
 * Язык ИНТЕРФЕЙСА и язык КОНТЕНТА (данные справочников, ?locale=) — независимы:
 * content-локаль может быть ЛЮБОЙ из SUPPORTED_LOCALES, даже если перевода
 * интерфейса под неё нет. Локаль ВНЕ этого реестра → интерфейс на русском
 * (DEFAULT_LOCALE), контент при этом остаётся на выбранной локали. Поэтому
 * нельзя «по умолчанию» накладывать EN на любую не-ru локаль — иначе новый
 * content-язык (kk и т.п.) получил бы английский интерфейс и фетч
 * отсутствующего <locale>.json.
 *
 * Добавить перевод интерфейса нового языка = одна строка здесь (+ TUI_LANGUAGES
 * ниже). Динамический import() оставляет словарь отдельным чанком — он не
 * попадает в бандл, пока соответствующая локаль не выбрана.
 */
const INTERFACE_DICTS: Partial<Record<AppLocale, () => Promise<typeof translations>>> = {
  en: () => import('../../../environments/translations.en').then(m => m.translationsEn),
};

/**
 * Язык Taiga UI для UI-локалей, у которых он есть. Локали без поддержки в
 * @taiga-ui/i18n (Taiga везёт лишь крупные языки) → русский TUI, не падение.
 */
const TUI_LANGUAGES: Partial<Record<AppLocale, typeof TUI_RUSSIAN_LANGUAGE>> = {
  en: TUI_ENGLISH_LANGUAGE,
};

/**
 * Инициализация языка интерфейса. Вызывается из main.ts ДО динамического
 * import('./app/app.module'): и конфиги форм (const FORM = [{title:
 * translations.x}]), и шаблонные $localize-константы вычисляются при
 * загрузке модулей, поэтому словарь и loadTranslations должны быть готовы
 * раньше любого кода приложения.
 */
export async function initAdminUiI18n(): Promise<void> {
  const locale = readUiLocale();
  // Данные локали (форматы date/number) нужны для КАЖДОЙ content-локали, которая
  // может стать LOCALE_ID (app.module: useFactory readUiLocale) — иначе DatePipe
  // под ней падает NG0701, даже если ИНТЕРФЕЙС остаётся на ru (kk — именно такой
  // content-only язык: ru-интерфейс, но kk-LOCALE_ID).
  registerLocaleData(ru);
  registerLocaleData(en);
  registerLocaleData(kk);

  const loadDict = INTERFACE_DICTS[locale];
  if (!loadDict) {
    // RU (встроенный) ИЛИ content-only локаль без перевода интерфейса:
    // интерфейс остаётся на русском. Не накладываем чужой словарь и не фетчим
    // отсутствующий <locale>.json.
    return;
  }

  // Параллельно: TS-словарь (чанк) и переводы шаблонов (<locale>.json) независимы.
  // Словарь: подменяем значения внутри ТОГО САМОГО объекта из compob2b-libs —
  // перевод получают и admin-импортёры (environments/translations.ru), и
  // внутренние потребители libs (SharedModule.validationMessages и т.п.).
  const [dict] = await Promise.all([
    loadDict(),
    fetch(`assets/i18n/${locale}.json`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(json => loadTranslations(json))
      // Без файла переводов не падаем: шаблоны останутся на русском.
      .catch(e => console.warn(`[i18n] Не удалось загрузить assets/i18n/${locale}.json`, e)),
  ]);
  Object.assign(translations, dict);
}

/** Язык Taiga UI для текущей UI-локали (провайдер TUI_LANGUAGE в app.module). */
export function uiTuiLanguage() {
  return TUI_LANGUAGES[readUiLocale()] ?? TUI_RUSSIAN_LANGUAGE;
}
